import { GTMData, PurchaseData, ViewItemData } from 'core/analytics/entities';
import { normalizeError } from 'core/common/errors';
import {
  ATTRS_ANALYTICS_EVENT_NAME,
  ATTRS_ANALYTICS_FUNNEL_LOCALE,
  ATTRS_ANALYTICS_FUNNEL_NAME,
  SpanName,
} from 'core/common/observability/entities';
import { ObservabilitySystem } from 'core/common/observability/interfaces';
import { FunnelSettingsServiceInterface } from 'core/common/services/funnel-settings/interfaces';
import { hash256 } from 'core/common/utils/hash';
import { GTMService } from '../../interfaces';

export class HttpGTMService implements GTMService {
  constructor(
    private readonly observabilitySystem: ObservabilitySystem,
    private readonly funnelSettingsService: FunnelSettingsServiceInterface,
  ) {}

  private get dataLayer() {
    return window.dataLayer || [];
  }

  pageView() {
    this.track({
      event: 'page_view',
    });
  }

  viewItem({ items, currency }: ViewItemData) {
    this.track({
      event: 'view_item',
      ecommerce: {
        currency,
        items: items.map((item) => ({
          item_id: item.productId,
          item_variant_id: item.productId,
          price: item.price,
        })),
      },
    });
  }

  optOtSellUserData() {
    const span = this.observabilitySystem.startSpan(SpanName.TRACK_ANALYTICS_GTM);

    try {
      span.addEvent('Pending');
      window.gtag('consent', 'update', {
        ad_storage: 'denied',
        analytics_storage: 'denied',
        functionality_storage: 'denied',
        personalization_storage: 'denied',
      });

      span.addEvent('Succeed');
      span.succeed();
    } catch (err) {
      const error = normalizeError(err);

      span.addEvent('Failed');
      span.fail(error);
    }
  }

  purchase({
    orderId,
    productId,
    price,
    ltvPredict,
    email,
    currency,
    paymentId,
    nebulaId,
  }: PurchaseData) {
    this.track({
      ecommerce: {
        currencyCode: 'USD',
        purchase: {
          actionField: {
            id: orderId,
            revenue: price,
          },
          products: [
            {
              id: productId,
              name: productId,
              price,
              quantity: 1,
            },
          ],
        },
      },
      eventCategory: 'ecommerce',
      eventAction: 'purchase',
      eventValue: 'revenue',
      eventNonInteraction: 'False',
      event: 'autoEvent',
    });
    this.track({
      event: 'purchase_ga4',
      user_params: {
        hashed_email: hash256(email),
        email,
        nebula_id: nebulaId,
      },
      event_params: {
        event_ID: orderId,
      },
      ecommerce: {
        transaction_id: orderId,
        payment_id: paymentId,
        value: ltvPredict,
        currency,
        items: [
          {
            item_name: productId,
            item_id: productId,
            price,
            quantity: 1,
          },
        ],
      },
    });
  }

  addToCart({ price, productId, email, orderId, currency }: GTMData) {
    this.track({
      ecommerce: {
        currencyCode: 'USD',
        add: {
          products: [
            {
              id: productId,
              name: productId,
              price,
              quantity: 1,
            },
          ],
        },
      },
      eventCategory: 'ecommerce',
      eventAction: 'product',
      eventLabel: 'addToCart',
      eventNonInteraction: 'False',
      event: 'autoEvent',
    });
    this.track({
      event: 'add_to_cart',
      user_params: {
        hashed_email: hash256(email),
      },
      event_params: {
        event_ID: orderId,
      },
      ecommerce: {
        currency,
        items: [
          {
            item_name: productId,
            item_id: productId,
            price,
            quantity: 1,
          },
        ],
      },
    });
  }

  initiateCheckout({ price, productId, email, orderId, currency }: GTMData) {
    this.track({
      ecommerce: {
        currencyCode: 'USD',
        checkout: {
          actionField: { step: 1, option: 'Initiation checkout' },
          products: [
            {
              id: productId,
              name: productId,
              price,
              quantity: 1,
            },
          ],
        },
      },
      event: 'autoEvent',
      eventNonInteraction: 'False',
      eventCategory: 'ecommerce',
      eventAction: 'Initiation checkout',
    });
    this.track({
      event: 'begin_checkout',
      user_params: {
        hashed_email: hash256(email),
      },
      event_params: {
        event_ID: orderId,
      },
      ecommerce: {
        currency,
        items: [
          {
            item_name: productId,
            item_id: productId,
            price,
            quantity: 1,
          },
        ],
      },
    });
  }

  addEmail(email: string, userId: string) {
    this.track({
      ecommerce: null,
      event: 'add_email',
      user_params: {
        hashed_email: hash256(email),
      },
      event_params: {
        event_ID: userId,
      },
    });
  }

  track(data: { event: string } & Record<string, unknown>) {
    const spanAttributes = {
      [ATTRS_ANALYTICS_EVENT_NAME]: data.event,
      [ATTRS_ANALYTICS_FUNNEL_NAME]: this.funnelSettingsService.getFunnelName(),
      [ATTRS_ANALYTICS_FUNNEL_LOCALE]: this.funnelSettingsService.getFunnelLocale(),
    };

    const span = this.observabilitySystem.startSpan(SpanName.TRACK_ANALYTICS_GTM);
    span.setAttributes(spanAttributes);

    try {
      span.addEvent('Pending');
      this.dataLayer.push(data);

      span.addEvent('Succeed');
      span.succeed();
    } catch (err) {
      const error = normalizeError(err);

      span.addEvent('Failed');
      span.fail(error);
    }
  }
}
